'use client';
import React, { useState } from 'react'; // Ensure useState is imported
import styles from './range.module.sass';
import cn from 'classnames';

export interface RangeProps extends React.HTMLAttributes<HTMLDivElement> {
    labels: Array<string>;
    text?: Array<string>;
    /** The zero based index */
    checked?: number;
    disabled?: boolean;
    onChangeHandler?: (newValue: number) => void;
}

export const RangeSlider = ({ labels, text, checked, disabled, onChangeHandler, ...props }: RangeProps) => {
    // Define needed values for input
    const min = 0,
        max = labels?.length - 1,
        step = 1;

    // Initialize state with `min` as the default value or checked if given
    const [value, setValue] = useState(() => {
        if (checked !== undefined && checked >= min && checked <= max) {
            return checked;
        }
        return min;
    });

    if (checked !== undefined && (checked > max || checked < min)) {
        console.error('Specified `checked` value is out of range.');
        return null;
    }

    const handleInput = (event: React.ChangeEvent<HTMLInputElement>) => {
        const newValue = parseInt(event.target.value, 10); // Convert input value to integer
        setValue(newValue);
        if (typeof onChangeHandler === 'function') onChangeHandler(newValue);
    };

    return (
        <div className={cn(styles.control, disabled && styles.disabled)} {...props}>
            <input
                type="range"
                min={min}
                max={max}
                step={step}
                name="parent-control"
                onInput={handleInput}
                value={value} // Bind the input's value to the state
                data-test-id="parent-control"
            />
            <span className={styles.range}></span>
            {labels && (
                <div className={styles.values}>
                    {labels.map((label, i) => (
                        <div
                            key={i}
                            className={cn(styles.valOption, {
                                [styles.selected]: value === min + i * step,
                                [styles.checked]: value > min + i * step,
                            })}
                        >
                            <span>{label}</span>
                        </div>
                    ))}
                </div>
            )}
            {text && (
                <div className={styles.text}>
                    {text.map((txt, i) => (
                        <div
                            key={i}
                            className={cn(styles.tOption, {
                                [styles.selected]: value === min + i * step,
                                [styles.checked]: value > min + i * step,
                            })}
                        >
                            {txt}
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
};
